import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Projects.css';
import HighlightedTitle from '../components/HighlightedTitle';
import ProjectPage from '../components/ProjectPage';

function DesignProjects() {
  const projects = React.useMemo(() => [
    {
      id: 'lavender',
      title: 'The Lavender Magazine Issue 16: Ritual',
      date: '2025',
      summary: 'Magazine Cover design for The Lavender Magazine',
      images: [{ type: "image", url: '/project_images_compressed/design/lavender/lavender-pink.jpg' }],
      link: 'https://www.route9.org/copy-of-on-air',
    },
    {
      id: 'prelapsarian',
      title: 'Prelapsarian',
      date: '2025',
      summary: 'Movie Poster & Social Media Designs',
      images: [{ type: "image", url: '/project_images_compressed/design/prelapsarian/prelapsarian1.jpg' }],
    },
    {
      id: 'priests-cant-date',
      title: 'Priests Can’t Date',
      date: '2025',
      summary: 'Movie Poster & Social Media Designs',
      images: [{ type: "image", url: '/project_images_compressed/design/priests-cant-date/priestscantdate.jpg' }],
    },
    {
      id: 'you-first',
      title: 'You First',
      date: '2025',
      summary: 'Movie Poster & Social Media Designs',
      images: [{ type: "image", url: '/project_images_compressed/design/you-first/youfirst2.jpg' }],
      awards: [
        "Best Film Thesis, Weselayn 2025",
      ]
    },
    {
      id: 'while-weve-still-got-feet',
      title: 'While We’ve Still Got Feet',
      date: '2024',
      summary: "Poster design for While We've Still Got Feet, a short film exploring grief, generational bonds, and the transformative power of caregiving. Inspired by the AIDS epidemic's legacy and intergenerational connection, the design captures the film's emotional depth and vibrant spirit.",
      images: [{ type: "image", url: '/project_images_compressed/design/while-weve-still-got-feet/while-weve-still-got-feet-main.jpg' },
        { type: "image", url: '/project_images_compressed/design/while-weve-still-got-feet/while-weve-still-got-feet3.jpg' },
        { type: "image", url: '/project_images_compressed/design/while-weve-still-got-feet/while-weve-still-got-feet4.jpg' },
        { type: "image", url: '/project_images_compressed/design/while-weve-still-got-feet/while-weve-still-got-feet5.jpg' },
        { type: "image", url: '/project_images_compressed/design/while-weve-still-got-feet/while-weve-still-got-feet6.jpg' },
        {type: "video", url: "https://www.youtube.com/watch?v=R6fKSMvLIA0"}],
      awards: [
        "Finalist Films Infest NYCIFIF 2024",
        "Official Selection Cannes Indie Shorts Award 2024",
        "Official Selection New York International Film Awards 2024",
        "Official Selection Indie Short Fest 2024"
      ],
    },
  ], []);

  const getSelectedProjectFromQuery = () => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    return projects.find((project) => project.id === projectId) || null;
  };

  const [selectedProject, setSelectedProject] = useState(getSelectedProjectFromQuery);
  const [imageLoaded, setImageLoaded] = useState(new Array(projects.length).fill(false));

  React.useEffect(() => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    setSelectedProject(projects.find((project) => project.id === projectId) || null);
  }, [projects]);

  const handleSelectProject = (project) => {
    const params = new URLSearchParams(window.location.search);
    params.set('project', project.id);
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', nextUrl);
    setSelectedProject(project);
  };

  const handleBack = () => {
    const nextUrl = window.location.pathname;
    window.history.pushState({}, '', nextUrl);
    setSelectedProject(null);
  };

  const handleImageLoad = (index) => {
    setImageLoaded((prev) => {
      const newLoadedState = [...prev];
      newLoadedState[index] = true;
      return newLoadedState;
    });
  };

  return (
    <AnimatePresence mode="wait">
      {selectedProject ? (
        <motion.div
          key="projectPage"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
        >
          <ProjectPage project={selectedProject} onBack={handleBack} />
        </motion.div>
      ) : (
        <motion.div
          key="projectList"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.5 }}
        >
          <div className="projects-container">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`project-item ${index % 2 === 0 ? 'left-first' : 'right-first'}`}
                onClick={() => handleSelectProject(project)}
              >
                <div className={`project-image-shell ${imageLoaded[index] ? 'is-loaded' : 'is-loading'}`}>
                  <img
                    src={project.images[0].url}
                    alt={project.title}
                    className="project-image"
                    onLoad={() => handleImageLoad(index)}
                    loading="lazy"
                  />
                </div>
                <div className="project-text">
                  <div className="project-title">
                    <HighlightedTitle text={project.title} />
                    <p>{project.date}</p>
                  </div>
                  <p className="project-summary">{project.summary}</p>
                  {project.awards && project.awards.map((award, i) => (
                    <p key={i} className="project-award">{award}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default DesignProjects;